import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Our Approach',
  sub_title: '取り組み',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '解決すべきは"使いづらさ"',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            転職マーケットにおいて、ユーザーは複数のエージェントサービスを併用する傾向にあります。そのため、「type転職エージェント」も他社との差別化を図ることが求められていました。特に、忙しく働く中で限られたスキマ時間を使って転職活動を進めるユーザーにとって、
            <span className={styles.textbold}>
              迷わずスムーズに活用できるアプリ体験を提供し、「type転職エージェント」を自然と選んでもらえる状態をつくることが重要な課題
            </span>
            となっていました。
          </p>
          <p>
            <span className={styles.space_top}>
              加えて、近年では若年層、PCを所有していない層の転職希望者が増加しており、特にスマホに慣れ親しんだデジタルネイティブ世代を中心に、転職活動をモバイルアプリで完結させたいという要望が高まっています。こうした背景から、スマホアプリにおいて「使いやすさ」「わかりやすさ」「利便性の高さ」が改めて重要視されるようになりました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach01.webp',
      alt: '解決すべきは使いづらさ　type転職エージェントアプリ',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <dl>
            <dt>リニューアル前の主な課題（ユーザーのペインポイント）</dt>
            <dd>UIが直感的ではなく使いづらい、機能が不足している</dd>
            <dd>情報入力や書類作成に手間がかかる</dd>
            <dd>スケジュール管理・タスク管理が煩雑</dd>
            <dd>面接に対する漠然とした不安</dd>
            <dd>アプリを使うメリットが不明瞭</dd>
            <dd>転職に対するモチベーションの維持が難しい</dd>
          </dl>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'ユーザーを深く理解し、本来あるべきアプリの価値を追求する',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            まずは、エキスパートレビューを実施して既存アプリの課題（画面遷移、UI、情報量、視覚的統一感など）を明確化しました。次に、業界知識やサービスへの理解を深めるため、同社のキャリアアドバイザーの方々に徹底的なヒアリングを実施。さらに、定量データに基づきペルソナとカスタマージャーニーマップを作成し、
            <span className={styles.textbold}>ユーザーの具体的な行動、感情、ペイン（悩み）を可視化しました</span>。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach02.webp',
      alt: 'リニューアル前のアプリに対するエキスパートレビュー',
      text: 'リニューアル前のアプリに対するエキスパートレビュー',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            これらの調査を基に、リニューアルの方針をコンセプト定義書にまとめ、クライアントとの共通認識を醸成した上でUIデザインに進むというプロセスを踏みました。これにより、単なる機能追加ではなく、ユーザーの課題を解決する本質的な価値を追求することができました。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach03.webp',
      alt: 'アプリの主な利用者・ターゲットの傾向から職業、性別、年齢ごとに3名のペルソナとそれぞれのカスタマージャーニーマップを作成',
      text: 'アプリの主な利用者・ターゲットの傾向から職業、性別、年齢ごとに3名のペルソナとそれぞれのカスタマージャーニーマップを作成',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>カスタマージャーニーマップからユーザーのペインを整理し、コア機能として以下の機能を盛り込みました。</p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach04.webp',
      alt: '今すべきことが一目でわかるやることリスト　直近の予定を把握できるスケジュール',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <dl>
            <dt>やることリスト・スケジュール</dt>
            <dd>
              ホーム画面上部の目立つ部分に「やることリスト」が表示され、ログインすると今やるべきことが一目でわかるようになっています。
            </dd>
            <dd>
              「スケジュール」セクションでは、応募した求人ごとに「日程調整中」や「アドバイザー確認中」などステータスがバッジで表示されるため、選考の進捗状況や次のアクションを簡単に把握できます。
            </dd>
            <dd>
              転職活動中は選考が同時進行することが多く、タスクやスケジュール管理が煩雑になりがちですが、この機能がユーザーの悩みを解消します。
            </dd>
          </dl>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach05.webp',
      alt: '日程調整の手間を省く　おまかせ日程調整',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <dl>
            <dt>おまかせ日程調整</dt>
            <dd>
              直近2週間の面接可能な日を入力すると、キャリアアドバイザーがユーザーに代わって日程を調整してくれる機能です。
            </dd>
            <dd>
              今回のリニューアルで追加した新機能で、ユーザーにとって煩わしい日程調整を簡単に済ませることができます。
            </dd>
          </dl>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach06.webp',
      alt: '選考の進捗管理ができる 「選考」タブ',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <dl>
            <dt>「選考」タブ</dt>
            <dd>
              画面上部に選考ステータスや応募総数などを一目で把握できるサマリーを表示し、スクロールすると応募した求人の詳細が確認できます。「求人を探して応募するアクションと、選考に進んだ求人を管理するアクションは別である」との考えのもと、これまでのナビゲーションにはなかった「選考」のタブを新設しました。
            </dd>
          </dl>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '伴走しながらフロントエンド内製化を促す“並走型支援”',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            キャリアデザインセンターは将来的な開発体制の内製化を目標としていましたが、リソースや技術スキル面に不安があり、難易度の高い機能開発や迅速なリリースを自社だけで行うのは難しい状況でした。
          </p>
          <p>
            <span className={styles.space_top}>
              そこで、Aitriaは
              <span className={styles.textbold}>単なる受託開発にとどまらず、クライアントと並走する形で開発を支援</span>
              。CI/CDツールの導入・移行を支える技術的支援や、UI設計から機能実装までを共に行う体制を構築しました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              この取り組みにより、キャリアデザインセンターは自社で開発できる領域を着実に拡大。外部依存を減らしつつ、必要な場面では高度な専門性を得られる“ハイブリッド開発体制”を構築することができました。その結果、アプリの品質向上とスピーディな運用体制が強化され、内製化のゆるぎない土台が形成されました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '劇的に向上したユーザー評価 – アプリストアの評価が 2.9 から 4.5 へ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/cdc/approach07.webp',
      alt: 'アプリストアの評価',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            リニューアル後、アプリストアの評価は劇的に改善されました。特に「やることリスト」や「おまかせ日程調整」といった新機能について、ユーザーからは高い評価が寄せられています。
          </p>
          <p>
            <span className={styles.space_top}>
              「複数の応募企業が混在してもタスクが整理され、次にやるべきことが一目でわかるようになった」「忙しい中でも、自分の空いている日程を選択するだけで自動で日程調整できて便利」といった声が多く見受けられ、ユーザーの課題が的確に解決されたことがわかります。
              <br />
              また、職務経歴書の作成機能も好評でした。従来、PCでの作業に心理的な負担を感じていたユーザーが、モバイル端末上で手軽に作成できるようになったことで、「移動中など、スキマ時間に対応できて助かる」との声も聞かれました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              こうした改善を通じて、「やることリスト」「おまかせ日程調整」といった
              <span className={styles.textbold}>
                コア機能がユーザーにきちんと届き、アプリの利用体験全体が向上したことが明確になりました
              </span>
              。結果的にアプリ利用者が内定を獲得しやすいようになり、サービスのエンゲージメントと転職成功率の向上に貢献しました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'クライアントの声',
    },
    {
      type: 'greyBox',
      namesub: '株式会社キャリアデザインセンター type転職エージェント事業部 システム局 局長',
      name: '齋藤 直輝氏',
      texts: [
        <>
          <p>
            「アプリの使いやすさ・利便性を向上させ、転職成功に欠かせないツールを目指す」をコンセプトに様々な要望を出させていただきましたが、一つ一つ丁寧に、かつロジカルにご提案いただき、納得感を持ちながらプロジェクトを進めることができました。
          </p>
          <p>
            <span className={styles.space_top}>
              そのおかげでリニューアル後のアプリは好評で、キャリアアドバイザーが自信を持って求職者の方にアプリをおススメしてくれるようになったこともあり、多くのユーザーがアプリを利用してくれるようになりました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              引き続きAitria社と協力しながらより使いやすいアプリを目指していきたいと思います。
            </span>
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
